import styled from '@emotion/styled'
import React from 'react'
import { Link } from 'react-router-dom'
import Routes from '../../../startup/routes'
import { Hugeicon } from '../../../icons/Hugeicon'

type Props = {
  organizationName: string
}

/**
 * Focused recovery screen shown when an owner opens a calendar whose
 * subscription is canceled. Instead of bouncing them to the marketing
 * landing page (no path back), we explain the calendar is paused and give a
 * single primary CTA to reactivate it through the existing checkout flow.
 */
const ReactivateCalendarScreen: React.FC<Props> = ({ organizationName }) => {
  const handleReactivate = () => {
    window.location.href = Routes.businessCalendarSignup()
  }

  return (
    <Wrapper>
      <BackLink to={Routes.businessCalendars()}>
        <BackArrow />
        All Calendars
      </BackLink>

      <Card>
        <IconCircle>
          <PauseIcon />
        </IconCircle>
        <Title>Your calendar is paused</Title>
        <Description>
          {organizationName}&apos;s calendar subscription was canceled, so it&apos;s no longer showing on your website.
          Your settings and events are saved — reactivate any time to bring it back online.
        </Description>
        <ReactivateButton onClick={handleReactivate}>Reactivate calendar</ReactivateButton>
        <SupportNote>
          Questions? <SupportLink href="mailto:support@civiclift.com">Contact support</SupportLink>
        </SupportNote>
      </Card>
    </Wrapper>
  )
}

export default ReactivateCalendarScreen

const BackArrow = () => (
  <Hugeicon name="arrow-left-long" size={16} />
)

const PauseIcon = () => (
  <Hugeicon name="pause" size={28} />
)

const Wrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px;
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  text-decoration: none;
  margin-bottom: 20px;
  transition: color 0.15s ease;

  &:hover {
    color: #374151;
  }
`

const Card = styled.div`
  max-width: 480px;
  margin: 40px auto 0;
  text-align: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 48px 32px;
`

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 12px;
`

const Description = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #6b7280;
  margin: 0 0 28px;
`

const ReactivateButton = styled.button`
  display: inline-block;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 32px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  }
`

const SupportNote = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 24px 0 0;
`

const SupportLink = styled.a`
  color: #3b82f6;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`
